import React, { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { SideSheet } from 'evergreen-ui';
import cn from 'classnames';

import { configurations, path, tabs } from 'pages/routes';

import * as userResource from 'resources/user/hooks';
import * as companyResource from 'resources/company/hooks';

import { useLogout } from 'resources/auth/hooks';

import getSideBarTabs from 'pages/_app/PageConfig/MainLayout/helpers/sideBarTabs';

import styles from './styles.module.scss';

const SideBar = ({
  isShown,
  onClose,
  disabled,
}) => {
  const router = useRouter();

  const { mutateAsync: logout } = useLogout();

  const { data: user } = userResource.useCurrentUser();
  const { data: company } = companyResource.useCompany(user?.company_id);

  const onNavigateToPage = useCallback((pagePath) => {
    router.push(pagePath);

    onClose();
  }, [router, onClose]);

  const onLogOut = useCallback(async () => {
    await logout();
  }, [logout]);

  const onTabClick = useCallback(async (tab) => {
    if (tab.link) {
      onNavigateToPage(tab.link);
      return;
    }

    if (tab.label === tabs.sideBarTabs.LOG_OUT) {
      await onLogOut();
    }

    if (tab.label === tabs.sideBarTabs.LOG_IN) {
      router.push(path.signIn);
    }
  }, [router, onNavigateToPage, onLogOut]);

  const sideButtonTabs = useMemo(() => getSideBarTabs(user, company), [user, company]);

  return (
    <SideSheet
      isShown={isShown}
      onCloseComplete={onClose}
      preventBodyScrolling
    >
      <p className={styles.title}>Menu</p>

      <div>
        {sideButtonTabs.map((tab, index) => {
          const isActiveTab = configurations(user, company).find((item) => item.path === router.route
          && item.sideBarTab === tab.label);

          return (
            <div
              key={tab.label}
              className={cn({
                [styles.tab]: true,
                [styles.activeTab]: isActiveTab,
                [styles.disabledTab]: disabled && tab.label !== tabs.sideBarTabs.LOG_IN,
                [styles.logOut]: index === sideButtonTabs.length - 1,
              })}
              onClick={() => onTabClick(tab)}
              aria-hidden
            >
              <p>{tab.label}</p>
            </div>
          );
        })}
      </div>
    </SideSheet>
  );
};

SideBar.propTypes = {
  isShown: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

SideBar.defaultProps = {
  disabled: false,
};

export default React.memo(SideBar);
